import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React, { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import ImportExportIcon from "@mui/icons-material/ImportExport";

import {
    ContentGetItemResponse,
    ParamsGetItem,
    ParamsGetListInvoice,
} from "../../constants/types";
import useItem from "../../services/queries/useItem";
import toRupiah from "../../utils/toRupiah";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import NoRowsImage from "../NoRowsImage/NoRowsImage";
import LoadingTabel from "../LoadingTabel/LoadingTabel";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import Chip from "@mui/material/Chip";
import DialogActions from "@mui/material/DialogActions";


import useUser from "../../services/queries/useUser";
import { Button, Dialog, DialogContent, DialogTitle, Stack } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useAuthStore } from "../../stores/authStore";

interface IPilihBarangStepProps {
    isOpenModalStokStep: boolean;
    closeModalStokStep: () => void;
    selectedItems: ContentGetItemResponse[];
    setSelectedItems: React.Dispatch<React.SetStateAction<ContentGetItemResponse[]>>;
    setParamsListInvoice: React.Dispatch<React.SetStateAction<ParamsGetListInvoice>>;
}

const PilihBarangStep = ({
    isOpenModalStokStep,
    closeModalStokStep,
    selectedItems,
    setSelectedItems,
    setParamsListInvoice,
}: IPilihBarangStepProps) => {
    const theme = useTheme();
    const isTabletScreen = useMediaQuery(theme.breakpoints.down("md"));
    const isPhoneScreen = useMediaQuery(theme.breakpoints.between("xs", "sm"));

    const ukmIdUser = useAuthStore((s) => s.ukmIdUser);

    const { data: user } = useUser();

    const initialParamsStok: ParamsGetItem = useMemo(
        () => ({
            ukmId: ukmIdUser && ukmIdUser[0],
            search: "",
            kategori: "",
            isShow: 0,
            size: 10,
            page: 1,
        }),
        [ukmIdUser],
    );

    const [paramsStok, setParamsStok] = useState(initialParamsStok);

    const { data: stok, isLoading: isLoadingStok } = useItem(paramsStok);

    const [search, setSearch] = useState<string>("");

    const [localSelectedItems, setLocalSelectedItems] = useState<ContentGetItemResponse[]>([]);

    const resetModalState = useCallback(() => {
        // Reset pilihan barang
        setLocalSelectedItems([]);
        
        // Reset search input
        setSearch("");
        
        // Reset params pencarian (kembali ke default)
        setParamsStok(prev => ({
            ...initialParamsStok,
            ukmId: prev.ukmId,
            page: 1,
        }));
    }, [initialParamsStok]);

    const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    const handleSearch = () => {
        setParamsStok((prev) => ({
            ...prev,
            search: search,
            page: 1,
        }));
    };

    const handleOnPagination = (page: number) => {
        setParamsStok((prev) => ({
            ...prev,
            page: page + 1,
        }));
    };

    const handleOnSizeChange = (size: number) => {
        setParamsStok((prev) => ({ ...prev, size, page: 1 }));
    };

    const handleSelectedProducts = (
        id: number,
        nama: string,
        sku: string,
        harga: number,
        stok: number,
        hargaBeli: number,
        rowData: any
    ) => {
        const isAlreadySelected = localSelectedItems.some(item => item.id === id);
        
        if (isAlreadySelected) {
            setLocalSelectedItems(prev => prev.filter(item => item.id !== id));
        } else {
            const newItem: ContentGetItemResponse = {
                id: rowData.id,
                nomer: rowData.nomer || 0,
                namaBarang: rowData.namaBarang,
                deskripsi: rowData.deskripsi || '',
                sku: rowData.sku,
                kategori: rowData.kategori || { id: 0, nama: '' },
                gambar: rowData.gambar || '',
                harga: rowData.harga,
                isActiveStok: rowData.isActiveStok || false,
                favorite: rowData.favorite || 0,
                stok: rowData.stok,
                isBundling: rowData.isBundling || 0,
                itemSatuan: rowData.itemSatuan || null,
                isShow: rowData.isShow || 0,
                detailBundling: rowData.detailBundling || [],
                hargaMultiple: rowData.hargaMultiple || [],
                totalHpp: rowData.totalHpp || 0,
                hpp: [],
                expAt: ""
            };
            setLocalSelectedItems(prev => [...prev, newItem]);
        }
    };

    const handleCariStok = () => {
        setSelectedItems(localSelectedItems);
        
        if (localSelectedItems.length > 0) {
            setParamsListInvoice((prev) => ({
                ...prev,
                daftarBarang: localSelectedItems.map((li) => li.id),
                page: 1,
            }));
        } else {
            setParamsListInvoice((prev) => ({
                ...prev,
                daftarBarang: undefined,
                page: 1,
            }));
        }
        
        resetModalState();
        closeModalStokStep();
    };

    const handleBatal = () => {
        setSelectedItems([]);
        setParamsListInvoice((prev) => ({
            ...prev,
            daftarBarang: undefined,
            page: 1,
        }));
        
        resetModalState();
        closeModalStokStep();
    };

    const handleClearSearch = () => {
        setSearch("");
        setParamsStok((prev) => ({
            ...prev,
            search: "",
            page: 1,
        }));
    };
    
    const columns: GridColDef[] = [
        {
            field: "namaBarang",
            headerName: "Nama Barang",
            width: 300,
            flex: 2,
            minWidth: 150,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="table-cell-trucate">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "kategori",
            headerName: "Kategori",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 125,
            renderCell: (params) => (
                <Tooltip title={params.value?.nama || '-'}>
                    <span className="table-cell-trucate">
                        {params.value?.nama || '-'}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "harga",
            headerName: "Harga",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 125,
            renderCell: (params) => (
                <Tooltip title={toRupiah(params.value ?? 0)}>
                    <span className="table-cell-trucate">
                        {toRupiah(params.value ?? 0)}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "sku",
            headerName: "SKU",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <Tooltip title={params.value || '-'}>
                    <span className="table-cell-trucate">{params.value || '-'}</span>
                </Tooltip>
            ),
        },
        {
            field: "stok",
            headerName: "Stok",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 100,
            renderCell: (params) => {
                const stokValue = params.value === null ? "∞" : params.value;
                return (
                    <Tooltip title={stokValue}>
                        <span className="table-cell-trucate">{stokValue}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: "aksi",
            headerName: "Aksi",
            headerAlign: "center",
            align: "center",
            minWidth: 100,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const isAlreadySelected = localSelectedItems.some(item => item.id === params.row.id);
                
                return (
                    <IconButton
                        onClick={() => {
                            handleSelectedProducts(
                                params.row.id,
                                params.row.namaBarang,
                                params.row.sku,
                                params.row.harga,
                                params.row.stok,
                                params.row.hpp && params.row.hpp.length > 0
                                    ? params.row.hpp.slice(-1)[0].hargaBeli
                                    : null,
                                params.row
                            );
                        }}
                        sx={{
                            minWidth: "unset",
                            padding: "6px",
                            width: "fit-content",
                        }}
                        size="small"
                        color="primary"
                    >
                        {isAlreadySelected ? (
                            <CheckBoxIcon />
                        ) : (
                            <CheckBoxOutlineBlankIcon />
                        )}
                    </IconButton>
                );
            },
        },
    ];

    
    useEffect(() => {
        if (user) {
            setParamsStok((prev) => ({
                ...prev,
                ukmId: ukmIdUser && ukmIdUser[0],
            }));
        }
    }, [ukmIdUser, user]);

    
    

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <Dialog
            maxWidth="md"
            fullScreen={isPhoneScreen}
            fullWidth={true}
            open={isOpenModalStokStep}
            onClose={handleBatal}
            scroll="body"
            PaperProps={{
                sx: {
                    borderRadius: 1,
                    maxHeight: '90vh',
                },
            }}
        >
            <DialogTitle>
                <Stack 
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                >
                    <Typography variant="h6" fontWeight="bold">
                        Cari Stok dalam Invoice
                    </Typography>

                    <IconButton onClick={handleBatal}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            
            <DialogContent dividers>
                <Fragment>
                    <Box>
                        {/* Filter Search */}
                        <Box
                            display="grid"
                            gridTemplateColumns={isTabletScreen ? "1fr" : "1fr 0.5fr"}
                            columnGap="1em"
                            rowGap={isTabletScreen ? "1em" : undefined}
                            mb={2}
                        >
                            <TextField
                                placeholder="Cari barang..."
                                size="small"
                                value={search}
                                onChange={handleChangeSearch}
                                onKeyPress={handleKeyPress}
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {search && (
                                                <IconButton 
                                                    onClick={handleClearSearch}
                                                    size="small"
                                                    sx={{ mr: 0.5 }}
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            <IconButton onClick={handleSearch}>
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    style: {
                                        backgroundColor: "white",
                                    },
                                }}
                            />
                        </Box>

                        {/* DataGrid untuk daftar stok */}
                        <Box
                            sx={{
                                height: 454,
                                width: "100%",
                                "& .MuiDataGrid-cell:focus-within": {
                                    outline: "none !important",
                                },
                                "& .MuiDataGrid-iconButtonContainer": {
                                    visibility: "visible",
                                    width: "0 !important",
                                },
                            }}
                        >
                            <DataGrid
                                density="compact"
                                rows={stok?.content ?? []}
                                columns={columns}
                                components={{
                                    ColumnUnsortedIcon: () => {
                                        return <ImportExportIcon />;
                                    },
                                    NoRowsOverlay: NoRowsImage,
                                    LoadingOverlay: LoadingTabel,
                                }}
                                disableColumnMenu
                                disableSelectionOnClick
                                rowsPerPageOptions={[10]}
                                paginationMode="server"
                                loading={isLoadingStok}
                                onPageSizeChange={handleOnSizeChange}
                                onPageChange={handleOnPagination}
                                rowCount={stok?.totalElements ?? 0}
                                page={paramsStok.page - 1}
                                pageSize={paramsStok.size}
                                getRowId={(row) => row.id}
                            />
                        </Box>

                        {/* Barang Terpilih Section */}
                        <Box mt={2} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography fontWeight="bold" gutterBottom>
                                Barang Terpilih ({localSelectedItems.length}):
                            </Typography>
                            {localSelectedItems.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                    {localSelectedItems.map((item) => (
                                        <Chip
                                            key={item.id}
                                            label={`${item.namaBarang} (${item.sku})`}
                                            size="small"
                                            onDelete={() => {
                                                setLocalSelectedItems(prev => prev.filter(i => i.id !== item.id));
                                            }}
                                            sx={{ maxWidth: '100%' }}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    Belum ada barang dipilih. Klik checkbox pada tabel untuk memilih barang.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Fragment>
            </DialogContent>
            
            <DialogActions sx={{ p: 2 }}>
                <Button 
                    onClick={handleBatal}
                    variant="outlined"
                >
                    Kembali
                </Button>
                <Button 
                    onClick={handleCariStok}
                    variant="contained"
                    disabled={localSelectedItems.length === 0}
                    startIcon={<SearchIcon />}
                >
                    Cari Stok ({localSelectedItems.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PilihBarangStep;
